import { isThenable } from './utils'

/**
 * Helper for class components that evaluate schema formulas which may return a
 * Promise.
 *
 * Formula evaluation is synchronous, so a Promise result can't be rendered
 * directly. This resolver runs the evaluation, returns the value synchronously
 * when it isn't a Promise, and otherwise resolves the Promise once per key,
 * caching the result and asking the component to re-render (via the `onResolved`
 * callback, typically `() => this.forceUpdate()`).
 */
export class AsyncFormulaResolver {
  private readonly results = new Map<string, any>()
  private readonly pending = new Set<string>()
  private disposed = false

  constructor(private readonly onResolved: () => void) {}

  public dispose(): void {
    this.disposed = true
  }

  /**
   * Evaluates a formula, transparently handling a Promise result.
   *
   * @param key Stable cache key for this formula in its current scope
   *   (e.g. `${entity.uniqueId}::${formula}`).
   * @param compute Thunk that evaluates the formula, possibly returning a Promise.
   * @returns The resolved (or synchronous) value, or `undefined` while a Promise
   *   for this key is still pending.
   */
  public resolve(key: string, compute: () => any): any {
    if (this.results.has(key)) {
      return this.results.get(key)
    }

    if (this.pending.has(key)) {
      return undefined
    }
    const rawValue = compute()
    if (!isThenable(rawValue)) {
      return rawValue
    }
    this.pending.add(key)
    Promise.resolve(rawValue).then(
      (resolved) => {
        this.pending.delete(key)
        if (this.disposed) {
          return
        }
        this.results.set(key, resolved)
        this.onResolved()
      },
      () => {
        this.pending.delete(key)
      }
    )
    return undefined
  }
}
